import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { AddQuestionDto } from "@/generated/model";
import { AddQuestionDtoQuestionType } from "@/generated/model";
import { axiosClient } from "@/lib/api";

// Re-export for convenience
export type { AddQuestionDto };
export {
  AddQuestionDtoQuestionType,
  AddQuestionDtoQuestionType as QuestionType,
} from "@/generated/model";

export interface Question {
  id: string;
  questionText: string;
  questionType: AddQuestionDtoQuestionType;
  isRequired: boolean;
  orderIndex: number;
  options?: Record<string, unknown>;
  validationRules?: Record<string, unknown>;
}

export interface Template {
  id: string;
  name: string;
  description: string | null;
  questions: Question[];
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  version: number;
  createdAt: string;
  updatedAt: string;
}

// Backwards compatibility - map questions to items format for existing UI
export interface ChecklistItem {
  id: string;
  label: string;
  category?: string;
  questionType?: AddQuestionDtoQuestionType;
}

export interface TemplateWithItems extends Omit<Template, "questions"> {
  items: ChecklistItem[];
}

// Helper to convert template questions to checklist items format
function templateToItemsFormat(template: Template): TemplateWithItems {
  return {
    ...template,
    items:
      template.questions?.map((q) => ({
        id: q.id,
        label: q.questionText,
        category: undefined,
        questionType: q.questionType,
      })) || [],
  };
}

export interface CreateTemplateQuestionInput {
  questionText: string;
  questionType: AddQuestionDtoQuestionType;
  isRequired: boolean;
  orderIndex: number;
  options?: Record<string, unknown>;
  validationRules?: Record<string, unknown>;
}

export interface CreateTemplateInput {
  name: string;
  description?: string;
  organisationId?: string;
  questions?: CreateTemplateQuestionInput[];
}

export interface AddQuestionInput {
  templateId: string;
  questionText: string;
  questionType?: AddQuestionDtoQuestionType;
  isRequired?: boolean;
  orderIndex: number;
  options?: Record<string, unknown>;
  validationRules?: Record<string, unknown>;
}

// Get all global templates (organisationId = null)
export function useGlobalTemplates(
  status?: "DRAFT" | "PUBLISHED" | "ARCHIVED",
  enabled = true,
) {
  return useQuery({
    queryKey: ["templates", "global", status],
    queryFn: async () => {
      const response = await axiosClient<Template[]>({
        url: `/v1/questionnaire-templates`,
        method: "GET",
        params: status ? { status } : undefined,
      });
      // Convert to items format for backwards compatibility
      return response.data.map(templateToItemsFormat);
    },
    enabled,
  });
}

// Create a new template with questions (returns just the id)
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateTemplateInput) => {
      const response = await axiosClient<{ id: string }>({
        url: `/v1/questionnaire-templates`,
        method: "POST",
        data: {
          name: input.name,
          description: input.description,
          organisationId: input.organisationId,
          questions: input.questions,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

// Delete/Archive a template
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axiosClient({
        url: `/v1/questionnaire-templates/${id}/archive`,
        method: "PATCH",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

// Publish a template
export function usePublishTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axiosClient<{ success: boolean }>({
        url: `/v1/questionnaire-templates/${id}/publish`,
        method: "PATCH",
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

// Import template JSON structure
export interface ImportTemplateInput {
  name: string;
  description?: string;
  organisationId?: string;
  questions: CreateTemplateQuestionInput[];
}

// Import a template from JSON
export function useImportTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ImportTemplateInput) => {
      const response = await axiosClient<{ id: string }>({
        url: `/v1/questionnaire-templates/import`,
        method: "POST",
        data: input,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}
